import React, { useState, useMemo } from "react";
import { ChevronsUpDown, ChevronLeft, ChevronRight, Check } from "lucide-react";

// Mock FormatDateTime function
const FormatDateTime = (dateString) => {
  return dateString;
};

export default function NewRejectedOrdersTable({
  rejectedOrdersData,
  filteredRejectedOrdersData = [],
  setFilterRejectedOrdersFno,
  setFilterRejectedOrdersEquity,
  sortOrder,
  setSortOrder,
  currentState,
  accountName = [], // This is the accountNames prop
  allUser = [], // Keeping this for backward compatibility
  currentPage,
  setCurrentPage,
  ...props
}) {
  // Use accountName as the primary source, fallback to allUser for backward compatibility
  const accountNames = accountName.length > 0 ? accountName : allUser;
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  console.log("AccountNames data:", accountNames);
  console.log("Rejected order data:", filteredRejectedOrdersData);

  // Sorting logic
  const sortedOrders = useMemo(() => {
    if (!sortConfig.key) return filteredRejectedOrdersData;
    
    return [...filteredRejectedOrdersData].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      if (sortConfig.key === 'symbol') {
        aValue = aValue.replace("NSE:", "");
        bValue = bValue.replace("NSE:", "");
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredRejectedOrdersData, sortConfig]);

  // Pagination
  const itemsPerPage = 10;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedOrders.length / itemsPerPage);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;
    
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }
    
    return pageNumbers;
  };

  const getSideColor = (side) => {
    return side === 1 ? "text-green-400" : "text-red-400";
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      "1": "CANCELLED",
      "Cancelled": "CANCELLED",
      "2": "TRADED", 
      "Traded": "TRADED",
      "4": "TRANSIT",
      "5": "REJECTED",
      "Rejected": "REJECTED", 
      "6": "PENDING",
      "Pending": "PENDING",
      "7": "EXPIRED"
    };
    return statusMap[status] || "STATUS UNKNOWN";
  };

  if (!filteredRejectedOrdersData || filteredRejectedOrdersData.length === 0) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400">
        <div className="text-lg font-medium">No Rejected Orders Found!!</div>
      </div>
    );
  }

  return (
    <div className="w-full h-[calc(100vh-60px)] bg-zinc-900 flex flex-col">
      {/* Sticky Header */}
      <div className="bg-zinc-800 border-b border-zinc-700 sticky top-0 z-10 shrink-0">
        <div className="grid grid-cols-8 gap-4 px-6 py-4 text-sm font-medium text-zinc-300">
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('symbol')}
          >
            <span>Symbol</span>
            <ChevronsUpDown className="w-4 h-4" />
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('orderDateTime')}
          >
            <span>Date</span>
            <ChevronsUpDown className="w-4 h-4" />
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('side')}
          >
            <span>Side</span>
            <ChevronsUpDown className="w-4 h-4" />
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('qty')}
          >
            <span>Quantity</span>
            <ChevronsUpDown className="w-4 h-4" />
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('status')}
          >
            <span>Status</span>
            <ChevronsUpDown className="w-4 h-4" />
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('message')}
          >
            <span>Rejected Reason</span>
            <ChevronsUpDown className="w-4 h-4" />
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('limitPrice')}
          >
            <span>Limit Price</span>
            <ChevronsUpDown className="w-4 h-4" />
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('strategy')}
          >
            <span>Strategy</span>
            <ChevronsUpDown className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Scrollable Table Body */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="min-w-full bg-zinc-900">
          <div className="divide-y divide-zinc-700">
            {currentItems.map((order, index) => (
              <div key={index} className="bg-zinc-900">
                {/* Main Order Row */}
                <div className="grid grid-cols-8 gap-4 px-6 py-4 text-sm text-zinc-200 hover:bg-zinc-800 transition-colors">
                  <div className="font-medium">
                    {order.symbol.replace("NSE:", "")}
                  </div>
                  <div>{FormatDateTime(order.orderDateTime)}</div>
                  <div className={`font-medium ${getSideColor(order.side)}`}>
                    {order.side === 1 ? "BUY" : "SELL"}
                  </div>
                  <div className="font-medium">{order.qty}</div>
                  <div className="text-red-400 font-medium">{getStatusDisplay(order.status)}</div>
                  <div className="text-xs text-zinc-400 truncate">{order.message}</div>
                  <div>₹{order.limitPrice}</div>
                  <div className="font-medium">{order.strategy}</div>
                </div>

                {/* Rejected Clients Only */}
                <div className="px-6 pb-4">
                  <div className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700">
                    <div className="bg-zinc-700 px-4 py-2">
                      <div className="text-xs font-medium text-zinc-300">
                        Rejected Clients
                      </div>
                    </div>
                    
                    <div className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {accountNames
                          .filter(user => order.clientIds.includes(user.client_id))
                          .map((user, userIndex) => (
                            <span 
                              key={userIndex}
                              className="text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 bg-red-800 border border-red-500"
                            >
                              <Check className="w-3 h-3 text-red-300" />
                              <span>{user.name}</span>
                            </span>
                          ))
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Fixed Pagination at Bottom */}
      <div className="bg-zinc-800 border-t border-zinc-700 shrink-0 sticky bottom-0 z-10">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="text-sm text-zinc-400">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedOrders.length)} of {sortedOrders.length} rejected orders
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center px-3 py-2 text-sm font-medium text-zinc-300 bg-zinc-700 rounded-lg hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </button>
            
            <div className="flex space-x-1">
              {getPageNumbers().map(number => (
                <button
                  key={number}
                  onClick={() => handlePageChange(number)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    currentPage === number
                      ? 'bg-blue-600 text-white'
                      : 'text-zinc-300 bg-zinc-700 hover:bg-zinc-600'
                  }`}
                >
                  {number}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center px-3 py-2 text-sm font-medium text-zinc-300 bg-zinc-700 rounded-lg hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}























// import React, { useState, useMemo } from "react";
// import { ChevronsUpDown, ChevronLeft, ChevronRight, Check } from "lucide-react";

// // Mock FormatDateTime function
// const FormatDateTime = (dateString) => {
//   return dateString;
// };

// export default function NewRejectedOrdersTable({
//   rejectedOrdersData,
//   filteredRejectedOrdersData = [],
//   setFilterRejectedOrdersFno,
//   setFilterRejectedOrdersEquity,
//   sortOrder,
//   setSortOrder,
//   currentState,
//   accountName = [], // This is the accountNames prop
//   allUser = [], // Keeping this for backward compatibility
//   currentPage,
//   setCurrentPage,
//   ...props
// }) {
//   // Use accountName as the primary source, fallback to allUser for backward compatibility
//   const accountNames = accountName.length > 0 ? accountName : allUser;
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

//   console.log("AccountNames data:", accountNames);
//   console.log("Rejected order data:", filteredRejectedOrdersData);

//   // Get unique multipliers for client grouping
//   const uniqueMultipliers = useMemo(() => {
//     return [...new Set(
//       accountNames
//         .map(user => user.index_multiplier)
//         .filter(multiplier => multiplier > 0)
//     )].sort((a, b) => a - b);
//   }, [accountNames]);

//   // Sorting logic
//   const sortedOrders = useMemo(() => {
//     if (!sortConfig.key) return filteredRejectedOrdersData;
    
//     return [...filteredRejectedOrdersData].sort((a, b) => {
//       let aValue = a[sortConfig.key];
//       let bValue = b[sortConfig.key];
      
//       if (sortConfig.key === 'symbol') {
//         aValue = aValue.replace("NSE:", "");
//         bValue = bValue.replace("NSE:", "");
//       }
      
//       if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
//       if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
//       return 0;
//     });
//   }, [filteredRejectedOrdersData, sortConfig]);

//   // Pagination
//   const itemsPerPage = 10;
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = sortedOrders.slice(indexOfFirstItem, indexOfLastItem);
//   const totalPages = Math.ceil(sortedOrders.length / itemsPerPage);

//   const handleSort = (key) => {
//     setSortConfig(prev => ({
//       key,
//       direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
//     }));
//   };

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//   };

//   const getPageNumbers = () => {
//     const pageNumbers = [];
//     const maxVisible = 5;
    
//     let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
//     let end = Math.min(totalPages, start + maxVisible - 1);
    
//     if (end - start + 1 < maxVisible) {
//       start = Math.max(1, end - maxVisible + 1);
//     }
    
//     for (let i = start; i <= end; i++) {
//       pageNumbers.push(i);
//     }
    
//     return pageNumbers;
//   };

//   const getSideColor = (side) => {
//     return side === 1 ? "text-green-400" : "text-red-400";
//   };

//   const getStatusDisplay = (status) => {
//     const statusMap = {
//       "1": "CANCELLED",
//       "Cancelled": "CANCELLED",
//       "2": "TRADED", 
//       "Traded": "TRADED",
//       "4": "TRANSIT",
//       "5": "REJECTED",
//       "Rejected": "REJECTED", 
//       "6": "PENDING",
//       "Pending": "PENDING",
//       "7": "EXPIRED"
//     };
//     return statusMap[status] || "STATUS UNKNOWN";
//   };

//   if (!filteredRejectedOrdersData || filteredRejectedOrdersData.length === 0) {
//     return (
//       <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400">
//         <div className="text-lg font-medium">No Rejected Orders Found!!</div>
//       </div>
//     );
//   }

//   return (
//     <div className="w-full h-[calc(100vh-60px)] bg-zinc-900 flex flex-col">
//       {/* Sticky Header */}
//       <div className="bg-zinc-800 border-b border-zinc-700 sticky top-0 z-10 shrink-0">
//         <div className="grid grid-cols-8 gap-4 px-6 py-4 text-sm font-medium text-zinc-300">
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('symbol')}
//           >
//             <span>Symbol</span>
//             <ChevronsUpDown className="w-4 h-4" />
//           </button>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('orderDateTime')}
//           >
//             <span>Date</span>
//             <ChevronsUpDown className="w-4 h-4" />
//           </button>
          
//           <div className="text-left">Side</div>
//           <div className="text-left">Quantity</div>
//           <div className="text-left">Status</div>
//           <div className="text-left">Rejected Reason</div>
//           <div className="text-left">Limit Price</div>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('strategy')}
//           >
//             <span>Strategy</span>
//             <ChevronsUpDown className="w-4 h-4" />
//           </button>
//         </div>
//       </div>

//       {/* Scrollable Table Body */}
//       <div className="flex-1 overflow-y-auto min-h-0">
//         <div className="min-w-full bg-zinc-900">
//           <div className="divide-y divide-zinc-700">
//             {currentItems.map((order, index) => (
//               <div key={index} className="bg-zinc-900">
//                 {/* Main Order Row */}
//                 <div className="grid grid-cols-8 gap-4 px-6 py-4 text-sm text-zinc-200 hover:bg-zinc-800 transition-colors">
//                   <div className="font-medium">
//                     {order.symbol.replace("NSE:", "")}
//                   </div>
//                   <div>{FormatDateTime(order.orderDateTime)}</div>
//                   <div className={`font-medium ${getSideColor(order.side)}`}>
//                     {order.side === 1 ? "BUY" : "SELL"}
//                   </div>
//                   <div className="font-medium">{order.qty}</div>
//                   <div className="text-red-400 font-medium">{getStatusDisplay(order.status)}</div>
//                   <div className="text-xs text-zinc-400 truncate">{order.message}</div>
//                   <div>₹{order.limitPrice}</div>
//                   <div className="font-medium">{order.strategy}</div>
//                 </div>

//                 {/* Rejected Clients Only */}
//                 <div className="px-6 pb-4">
//                   <div className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700">
//                     <div className="bg-zinc-700 px-4 py-2">
//                       <div className="text-xs font-medium text-zinc-300">
//                         Rejected Clients
//                       </div>
//                     </div>
                    
//                     <div className="px-4 py-3">
//                       <div className="flex flex-wrap gap-2">
//                         {accountNames
//                           .filter(user => order.clientIds.includes(user.client_id))
//                           .map((user, userIndex) => (
//                             <span 
//                               key={userIndex}
//                               className="text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 bg-red-800 border border-red-500"
//                             >
//                               <Check className="w-3 h-3 text-red-300" />
//                               <span>{user.name}</span>
//                             </span>
//                           ))
//                         }
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </div>

//       {/* Fixed Pagination at Bottom */}
//       <div className="bg-zinc-800 border-t border-zinc-700 shrink-0 sticky bottom-0 z-10">
//         <div className="flex items-center justify-between px-6 py-4">
//           <div className="text-sm text-zinc-400">
//             Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedOrders.length)} of {sortedOrders.length} rejected orders
//           </div>
          
//           <div className="flex items-center space-x-2">
//             <button
//               onClick={() => handlePageChange(currentPage - 1)}
//               disabled={currentPage === 1}
//               className="flex items-center px-3 py-2 text-sm font-medium text-zinc-300 bg-zinc-700 rounded-lg hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
//             >
//               <ChevronLeft className="w-4 h-4 mr-1" />
//               Previous
//             </button>
            
//             <div className="flex space-x-1">
//               {getPageNumbers().map(number => (
//                 <button
//                   key={number}
//                   onClick={() => handlePageChange(number)}
//                   className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
//                     currentPage === number
//                       ? 'bg-blue-600 text-white'
//                       : 'text-zinc-300 bg-zinc-700 hover:bg-zinc-600'
//                   }`}
//                 >
//                   {number}
//                 </button>
//               ))}
//             </div>
            
//             <button
//               onClick={() => handlePageChange(currentPage + 1)}
//               disabled={currentPage === totalPages}
//               className="flex items-center px-3 py-2 text-sm font-medium text-zinc-300 bg-zinc-700 rounded-lg hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
//             >
//               Next
//               <ChevronRight className="w-4 h-4 ml-1" />
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }










































































// import React, { useState, useMemo } from "react";
// import { ChevronsUpDown, ChevronLeft, ChevronRight, Check } from "lucide-react";

// // Mock FormatDateTime function
// const FormatDateTime = (dateString) => {
//   return dateString;
// };

// // Lot size configuration
// const LOT_SIZE_CONFIG = {
//   NIFTY: 75,
//   BANKNIFTY: 30
// };

// // Helper function to get lot size based on symbol
// const getLotSize = (symbol) => {
//   const upperSymbol = symbol.toUpperCase();
//   if (upperSymbol.includes('BANKNIFTY')) {
//     return LOT_SIZE_CONFIG.BANKNIFTY; // BANKNIFTY takes precedence
//   } else if (upperSymbol.includes('NIFTY')) {
//     return LOT_SIZE_CONFIG.NIFTY;
//   }
//   return null; // Stock option
// };

// // Helper function to check if symbol is index option
// const isIndexOption = (symbol) => {
//   const upperSymbol = symbol.toUpperCase();
//   return upperSymbol.includes('NIFTY') || upperSymbol.includes('BANKNIFTY');
// };

// export default function NewRejectedOrdersTable({
//   rejectedOrdersData,
//   filteredRejectedOrdersData = [],
//   setFilterRejectedOrdersFno,
//   setFilterRejectedOrdersEquity,
//   sortOrder,
//   setSortOrder,
//   currentState,
//   accountName = [], // This is the accountNames prop
//   allUser = [], // Keeping this for backward compatibility
//   currentPage,
//   setCurrentPage,
//   ...props
// }) {
//   // Use accountName as the primary source, fallback to allUser for backward compatibility
//   const accountNames = accountName.length > 0 ? accountName : allUser;
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

//   console.log("AccountNames data:", accountNames);
//   console.log("Rejected order symbol detection test:", filteredRejectedOrdersData.map(order => ({
//     symbol: order.symbol,
//     isIndex: isIndexOption(order.symbol),
//     lotSize: getLotSize(order.symbol)
//   })));

//   // Group rejected orders by symbol + strategy + side
//   const groupedOrders = useMemo(() => {
//     const groups = {};
    
//     filteredRejectedOrdersData.forEach(order => {
//       const key = `${order.symbol}-${order.strategy}-${order.side}`;
      
//       if (!groups[key]) {
//         groups[key] = {
//           symbol: order.symbol,
//           strategy: order.strategy,
//           side: order.side,
//           orderDateTime: order.orderDateTime,
//           limitPrice: order.limitPrice,
//           stopPrice: order.stopPrice,
//           status: order.status,
//           message: order.message,
//           actualQuantities: {}, // Store actual quantities from orders
//           clientOrderMap: {} // Map client to their actual quantities
//         };
//       }
      
//       // Store actual quantities and client mappings
//       order.clientIds.forEach(clientId => {
//         if (!groups[key].clientOrderMap[clientId]) {
//           groups[key].clientOrderMap[clientId] = [];
//         }
//         groups[key].clientOrderMap[clientId].push(order.qty);
//       });
      
//       if (!groups[key].actualQuantities[order.qty]) {
//         groups[key].actualQuantities[order.qty] = [];
//       }
//       groups[key].actualQuantities[order.qty].push(...order.clientIds);
//     });
    
//     // Remove duplicates from clientOrderMap
//     Object.values(groups).forEach(group => {
//       Object.keys(group.clientOrderMap).forEach(clientId => {
//         group.clientOrderMap[clientId] = [...new Set(group.clientOrderMap[clientId])];
//       });
//     });
    
//     return Object.values(groups);
//   }, [filteredRejectedOrdersData]);

//   // Generate multiplier tiers for each group
//   const enhancedGroupedOrders = useMemo(() => {
//     return groupedOrders.map(group => {
//       const isIndex = isIndexOption(group.symbol);
      
//       if (isIndex) {
//         // For index options, use multiplier logic
//         const lotSize = getLotSize(group.symbol);
        
//         // For BANKNIFTY, force all clients to have multiplier 1
//         // For other NIFTY symbols, use actual multipliers
//         const uniqueMultipliers = group.symbol.toUpperCase().includes('BANKNIFTY') 
//           ? [1] // Force multiplier 1 for BANKNIFTY
//           : [...new Set(
//               accountNames
//                 .map(user => user.index_multiplier)
//                 .filter(multiplier => multiplier > 0)
//             )].sort((a, b) => a - b);
        
//         console.log("Unique multipliers for rejected order", group.symbol, ":", uniqueMultipliers);
        
//         const multiplierTiers = uniqueMultipliers.map(multiplier => {
//           const calculatedQty = multiplier * lotSize;
          
//           // Get clients who actually have this quantity
//           const clientsWithThisQty = group.actualQuantities[calculatedQty] || [];
          
//           return {
//             multiplier,
//             quantity: calculatedQty,
//             // For BANKNIFTY, show all clients regardless of their actual multiplier
//             // For other NIFTY symbols, filter by actual multiplier
//             allClients: group.symbol.toUpperCase().includes('BANKNIFTY') 
//               ? accountNames // Show all clients for BANKNIFTY
//               : accountNames.filter(user => user.index_multiplier === multiplier),
//             clientsWithOrder: clientsWithThisQty
//           };
//         });
        
//         return {
//           ...group,
//           multiplierTiers,
//           isIndexOption: true
//         };
//       } else {
//         // For stock options, keep current logic
//         const quantityTiers = Object.entries(group.actualQuantities)
//           .map(([qty, clientIds]) => ({
//             quantity: parseInt(qty),
//             clientIds: [...new Set(clientIds)]
//           }))
//           .sort((a, b) => a.quantity - b.quantity);
          
//         return {
//           ...group,
//           quantityTiers,
//           isIndexOption: false
//         };
//       }
//     });
//   }, [groupedOrders, accountNames]);

//   console.log("Enhanced grouped rejected orders:", enhancedGroupedOrders);

//   // Sorting logic
//   const sortedOrders = useMemo(() => {
//     if (!sortConfig.key) return enhancedGroupedOrders;
    
//     return [...enhancedGroupedOrders].sort((a, b) => {
//       let aValue = a[sortConfig.key];
//       let bValue = b[sortConfig.key];
      
//       if (sortConfig.key === 'symbol') {
//         aValue = aValue.replace("NSE:", "");
//         bValue = bValue.replace("NSE:", "");
//       }
      
//       if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
//       if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
//       return 0;
//     });
//   }, [enhancedGroupedOrders, sortConfig]);

//   // Pagination
//   const itemsPerPage = 10;
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = sortedOrders.slice(indexOfFirstItem, indexOfLastItem);
//   const totalPages = Math.ceil(sortedOrders.length / itemsPerPage);

//   const handleSort = (key) => {
//     setSortConfig(prev => ({
//       key,
//       direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
//     }));
//   };

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//   };

//   const getPageNumbers = () => {
//     const pageNumbers = [];
//     const maxVisible = 5;
    
//     let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
//     let end = Math.min(totalPages, start + maxVisible - 1);
    
//     if (end - start + 1 < maxVisible) {
//       start = Math.max(1, end - maxVisible + 1);
//     }
    
//     for (let i = start; i <= end; i++) {
//       pageNumbers.push(i);
//     }
    
//     return pageNumbers;
//   };

//   const getSideColor = (side) => {
//     return side === 1 ? "text-green-400" : "text-red-400";
//   };

//   const getStatusDisplay = (status) => {
//     const statusMap = {
//       "1": "CANCELLED",
//       "Cancelled": "CANCELLED",
//       "2": "TRADED", 
//       "Traded": "TRADED",
//       "4": "TRANSIT",
//       "5": "REJECTED",
//       "Rejected": "REJECTED", 
//       "6": "PENDING",
//       "Pending": "PENDING",
//       "7": "EXPIRED"
//     };
//     return statusMap[status] || "STATUS UNKNOWN";
//   };

//   if (!filteredRejectedOrdersData || filteredRejectedOrdersData.length === 0) {
//     return (
//       <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400">
//         <div className="text-lg font-medium">No Rejected Orders Found!!</div>
//       </div>
//     );
//   }

//   return (
//     <div className="w-full h-[calc(100vh-60px)] bg-zinc-900 flex flex-col">
//       {/* Sticky Header */}
//       <div className="bg-zinc-800 border-b border-zinc-700 sticky top-0 z-10 shrink-0">
//         <div className="grid grid-cols-8 gap-4 px-6 py-4 text-sm font-medium text-zinc-300">
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('symbol')}
//           >
//             <span>Symbol</span>
//             <ChevronsUpDown className="w-4 h-4" />
//           </button>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('orderDateTime')}
//           >
//             <span>Date</span>
//             <ChevronsUpDown className="w-4 h-4" />
//           </button>
          
//           <div className="text-left">Side</div>
//           <div className="text-left">Quantity</div>
//           <div className="text-left">Status</div>
//           <div className="text-left">Rejected Reason</div>
//           <div className="text-left">Limit Price</div>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('strategy')}
//           >
//             <span>Strategy</span>
//             <ChevronsUpDown className="w-4 h-4" />
//           </button>
//         </div>
//       </div>

//       {/* Scrollable Table Body */}
//       <div className="flex-1 overflow-y-auto min-h-0">
//         <div className="min-w-full bg-zinc-900">
//           <div className="divide-y divide-zinc-700">
//             {currentItems.map((order, index) => (
//               <div key={index} className="bg-zinc-900">
//                 {/* Main Order Row */}
//                 <div className="grid grid-cols-8 gap-4 px-6 py-4 text-sm text-zinc-200 hover:bg-zinc-800 transition-colors">
//                   <div className="font-medium">
//                     {order.symbol.replace("NSE:", "")}
//                   </div>
//                   <div>{FormatDateTime(order.orderDateTime)}</div>
//                   <div className={`font-medium ${getSideColor(order.side)}`}>
//                     {order.side === 1 ? "BUY" : "SELL"}
//                   </div>
//                   <div>{order.isIndexOption ? 
//                     order.multiplierTiers?.reduce((sum, tier) => sum + (tier.allClients.length * tier.quantity), 0) :
//                     order.quantityTiers?.reduce((sum, tier) => sum + tier.quantity, 0)
//                   }</div>
//                   <div className="text-red-400 font-medium">{getStatusDisplay(order.status)}</div>
//                   <div className="text-xs text-zinc-400 truncate">{order.message}</div>
//                   <div>₹{order.limitPrice}</div>
//                   <div className="font-medium">{order.strategy}</div>
//                 </div>

//                 {/* Enhanced Quantity-Client Sub Table */}
//                 <div className="px-6 pb-4">
//                   <div className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700">
//                     {order.isIndexOption ? (
//                       // Index Options - Multiplier Logic
//                       <>
//                         <div className="bg-zinc-700 px-4 py-2">
//                           <div className="flex gap-4 text-xs font-medium text-zinc-300">
//                             <div className="min-w-[125px] flex items-start justify-start">Index Multiplier</div>
//                             <div className="min-w-[125px] flex items-start justify-start">Quantity</div>
//                             <div className="min-w-[140px] flex items-start justify-start">Clients</div>
//                           </div>
//                         </div>
                        
//                         <div className="divide-y divide-zinc-700">
//                           {order.multiplierTiers.map((tier, tierIndex) => (
//                             <div key={tierIndex} className="grid grid-cols-[120px,120px,1fr] gap-4 px-4 py-3 text-sm items-start">
//                               <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
//                                 {tier.multiplier}
//                               </div>
//                               <div className="font-medium text-emerald-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
//                                 {tier.quantity}
//                               </div>
//                               <div className="flex flex-wrap gap-2">
//                                 {tier.allClients.map((user, userIndex) => {
//                                   const hasOrder = tier.clientsWithOrder.includes(user.client_id);
//                                   return (
//                                     <span 
//                                       key={userIndex}
//                                       className={`text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 ${
//                                         hasOrder ? 'bg-red-800 border border-red-500' : 'bg-zinc-700'
//                                       }`}
//                                     >
//                                       {hasOrder && (
//                                         <Check className="w-3 h-3 text-red-300" />
//                                       )}
//                                       <span>{user.name}</span>
//                                     </span>
//                                   );
//                                 })}
//                               </div>
//                             </div>
//                           ))}
//                         </div>
//                       </>
//                     ) : (
//                       // Stock Options - Current Logic
//                       <>
//                         <div className="bg-zinc-700 px-4 py-2">
//                           <div className="grid grid-cols-2 gap-4 text-xs font-medium text-zinc-300">
//                             <div>Quantity</div>
//                             <div>Clients</div>
//                           </div>
//                         </div>
                        
//                         <div className="divide-y divide-zinc-700">
//                           {order.quantityTiers.map((tier, tierIndex) => (
//                             <div key={tierIndex} className="grid grid-cols-[120px,1fr] gap-4 px-4 py-3 text-sm items-start">
//                               <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
//                                 {tier.quantity}
//                               </div>
//                               <div className="flex flex-wrap gap-2">
//                                 {accountNames
//                                   .filter(user => tier.clientIds.includes(user.client_id))
//                                   .map((user, userIndex) => {
//                                     const hasOrder = tier.clientIds.includes(user.client_id);
//                                     return (
//                                       <span 
//                                         key={userIndex}
//                                         className={`text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 ${
//                                           hasOrder ? 'bg-red-800 border border-red-500' : 'bg-zinc-700'
//                                         }`}
//                                       >
//                                         {hasOrder && (
//                                           <Check className="w-3 h-3 text-red-300" />
//                                         )}
//                                         <span>{user.name}</span>
//                                       </span>
//                                     );
//                                   })
//                                 }
//                               </div>
//                             </div>
//                           ))}
//                         </div>
//                       </>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </div>

//       {/* Fixed Pagination at Bottom */}
//       <div className="bg-zinc-800 border-t border-zinc-700 shrink-0 sticky bottom-0 z-10">
//         <div className="flex items-center justify-between px-6 py-4">
//           <div className="text-sm text-zinc-400">
//             Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedOrders.length)} of {sortedOrders.length} rejected orders
//           </div>
          
//           <div className="flex items-center space-x-2">
//             <button
//               onClick={() => handlePageChange(currentPage - 1)}
//               disabled={currentPage === 1}
//               className="flex items-center px-3 py-2 text-sm font-medium text-zinc-300 bg-zinc-700 rounded-lg hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
//             >
//               <ChevronLeft className="w-4 h-4 mr-1" />
//               Previous
//             </button>
            
//             <div className="flex space-x-1">
//               {getPageNumbers().map(number => (
//                 <button
//                   key={number}
//                   onClick={() => handlePageChange(number)}
//                   className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
//                     currentPage === number
//                       ? 'bg-blue-600 text-white'
//                       : 'text-zinc-300 bg-zinc-700 hover:bg-zinc-600'
//                   }`}
//                 >
//                   {number}
//                 </button>
//               ))}
//             </div>
            
//             <button
//               onClick={() => handlePageChange(currentPage + 1)}
//               disabled={currentPage === totalPages}
//               className="flex items-center px-3 py-2 text-sm font-medium text-zinc-300 bg-zinc-700 rounded-lg hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
//             >
//               Next
//               <ChevronRight className="w-4 h-4 ml-1" />
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }


